import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import ConfirmationPopup from "../../../packages/ConfirmationPopup";
import CustomModal from "../../../packages/CustomModal";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../packages/WPMessageBanner/WPMessageBanner";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";

import UtilityCostFields from "../UtilityCostFields";
import {
  convertUtilityFormValues,
  FIELD_KEYS,
  getInitialValues,
  getUtilityFieldRanges,
} from "../utilityCosts.config";
import {
  fetchUtilityCostsData,
  toUtilityFormValues,
  updateUtilityCostsData,
} from "./UtilityCostsDialog.api";

import { DEFAULT_CURRENCY_CODE } from "../../../constants/currencies.constants";
import {
  CustomModalButtons,
  FEATURE_SCOPE,
  PENDING_PLANT_BASELINE_UNITS_KEY,
} from "../../../constants/global.constants";
import { UNITS } from "../../../constants/units.constant";

import IconWithTooltip from "../../../common/styles/components/headings/IconWithTooltip";

import { getItemSession } from "../../../utils/session";
import { isNullOrUndefinedOrEmpty } from "../../../utils/Utils";

import { storePlantUtilityCosts } from "../../../Store/TemplateStore/template.action";

import "./index.scss";

const UtilityCostsDialog = ({
  open,
  onClose,
  onUtilityCostsSaved,
  scope = FEATURE_SCOPE.PLANT,
  currency = DEFAULT_CURRENCY_CODE,
  volume = UNITS.CUBIC_METER,
  conversionRate = 1,
  enablePendingUnitConversion = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { plantUtilityCosts } = useSelector((state) => ({
    plantUtilityCosts: state.plantUtilityCosts,
  }));
  const isUserScope = scope === FEATURE_SCOPE.USER;

  const [initialValues, setInitialValues] = useState(getInitialValues);
  const [values, setValues] = useState(getInitialValues);
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const conversionContextRef = useRef(null);
  const plantUtilityCostsAtOpenRef = useRef(null);

  const toStoreUtilityCostsFromForm = (formValues = {}) => ({
    water_price: Number(formValues?.priceOfWater) || 0,
    downtime_cost: Number(formValues?.costOfDowntime) || 0,
    energy_price: Number(formValues?.costOfElectricity) || 0,
  });

  const activeCurrency = isUserScope ? DEFAULT_CURRENCY_CODE : currency;
  const activeVolume = isUserScope ? UNITS.CUBIC_METER : volume;
  const activeConversionRate = isUserScope ? 1 : Number(conversionRate) || 1;

  const sessionBaselineUnits = useMemo(() => {
    try {
      const rawValue = getItemSession(PENDING_PLANT_BASELINE_UNITS_KEY);
      return rawValue ? JSON.parse(rawValue) : null;
    } catch (error) {
      return null;
    }
  }, [open]);

  const hasSessionBaseline =
    !isUserScope &&
    !!sessionBaselineUnits &&
    (sessionBaselineUnits?.currency ||
      sessionBaselineUnits?.volume ||
      sessionBaselineUnits?.conversion_rate);

  const persistedCurrency = isUserScope
    ? DEFAULT_CURRENCY_CODE
    : hasSessionBaseline
    ? sessionBaselineUnits?.currency || activeCurrency
    : activeCurrency;
  const persistedVolume = isUserScope
    ? UNITS.CUBIC_METER
    : hasSessionBaseline
    ? sessionBaselineUnits?.volume || activeVolume
    : activeVolume;
  const persistedConversionRate = isUserScope
    ? 1
    : hasSessionBaseline
    ? Number(sessionBaselineUnits?.conversion_rate) || activeConversionRate
    : activeConversionRate;

  const hasPendingUnitChanges =
    !isUserScope &&
    (persistedCurrency !== activeCurrency ||
      persistedVolume !== activeVolume ||
      persistedConversionRate !== activeConversionRate);

  const shouldUseConvertedView =
    !isUserScope && enablePendingUnitConversion && hasPendingUnitChanges;

  const getLiveConversionContext = () => ({
    shouldUseConvertedView,
    persistedCurrency,
    persistedVolume,
    persistedConversionRate,
    activeCurrency,
    activeVolume,
    activeConversionRate,
  });

  const fieldRanges = useMemo(
    () =>
      getUtilityFieldRanges(activeCurrency, activeVolume, activeConversionRate),
    [activeCurrency, activeVolume, activeConversionRate],
  );

  const hasErrors = Object.values(errors).some(
    (error) => error !== null && error !== "" && error !== undefined,
  );

  const hasEmptyRequiredFields = FIELD_KEYS.some((key) =>
    isNullOrUndefinedOrEmpty(values[key]),
  );

  const hasChanges = useMemo(
    () => FIELD_KEYS.some((key) => values[key] !== initialValues[key]),
    [values, initialValues],
  );

  const isMakeDefaultConfirmation = !isUserScope && !hasChanges;

  useEffect(() => {
    if (!open) {
      conversionContextRef.current = null;
      plantUtilityCostsAtOpenRef.current = null;
      return;
    }

    conversionContextRef.current = getLiveConversionContext();
    plantUtilityCostsAtOpenRef.current = plantUtilityCosts;
  }, [open]);

  const confirmDialogConfig = isMakeDefaultConfirmation
    ? {
        header: t("makeDefault.confirmHeader"),
        description: t("makeDefault.utilityConfirmDescription"),
        confirmBtn: t("makeDefault.confirmBtn"),
        cancelBtn: t("ValueStrings.cancel"),
      }
    : {
        header: t(`${scope}.confirmHeader`),
        description: t(`${scope}.confirmDescription`),
        confirmBtn: t("ValueStrings.save&Update"),
        cancelBtn: t("ValueStrings.dontSave"),
      };

  useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;

    const loadUtilityCosts = async () => {
      const conversionContext =
        conversionContextRef.current || getLiveConversionContext();

      setIsLoading(true);

      const cachedPlantUtilityCosts = plantUtilityCostsAtOpenRef.current;

      const hasCachedPlantUtilityCosts =
        !isUserScope &&
        (cachedPlantUtilityCosts?.water_price !== undefined ||
          cachedPlantUtilityCosts?.downtime_cost !== undefined ||
          cachedPlantUtilityCosts?.energy_price !== undefined);

      const utilityCosts = hasCachedPlantUtilityCosts
        ? toUtilityFormValues(cachedPlantUtilityCosts)
        : await fetchUtilityCostsData(t, isUserScope);

      if (!isMounted) {
        return;
      }

      const nextInitialValues = utilityCosts || getInitialValues();
      const nextDisplayValues = conversionContext.shouldUseConvertedView
        ? convertUtilityFormValues(
            nextInitialValues,
            {
              currency: conversionContext.persistedCurrency,
              volume: conversionContext.persistedVolume,
              conversion_rate: conversionContext.persistedConversionRate,
            },
            {
              currency: conversionContext.activeCurrency,
              volume: conversionContext.activeVolume,
              conversion_rate: conversionContext.activeConversionRate,
            },
          )
        : nextInitialValues;

      if (!isUserScope) {
        dispatch(
          storePlantUtilityCosts(
            toStoreUtilityCostsFromForm(nextInitialValues),
          ),
        );
      }

      setInitialValues(nextDisplayValues);
      setValues(nextDisplayValues);
      setErrors({});
      setIsLoading(false);
    };

    loadUtilityCosts();

    return () => {
      isMounted = false;
    };
  }, [open, scope, t, dispatch, isUserScope]);

  const disableActions = hasErrors || hasEmptyRequiredFields || !hasChanges;
  const disableMakeDefault = hasErrors || hasEmptyRequiredFields || hasChanges;

  const handleChange = (fieldKey, event) => {
    const value = event?.target?.value ?? event;
    setValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleValidationError = (fieldKey, errorMessage) => {
    setErrors((prev) => ({ ...prev, [fieldKey]: errorMessage }));
  };

  const handleModalClose = ({ target }) => {
    const buttonId = target?.id;
    if (buttonId === CustomModalButtons.CONFIRM) {
      setShowConfirmation(true);
    } else if (
      buttonId === CustomModalButtons.CANCEL ||
      buttonId === CustomModalButtons.CLOSE
    ) {
      onClose?.();
    }
  };

  const handleConfirmationClose = async (buttonId) => {
    setShowConfirmation(false);

    if (buttonId === CustomModalButtons.CONFIRM) {
      const conversionContext =
        conversionContextRef.current || getLiveConversionContext();

      setIsLoading(true);

      const valuesForSave = conversionContext.shouldUseConvertedView
        ? convertUtilityFormValues(
            values,
            {
              currency: conversionContext.activeCurrency,
              volume: conversionContext.activeVolume,
              conversion_rate: conversionContext.activeConversionRate,
            },
            {
              currency: conversionContext.persistedCurrency,
              volume: conversionContext.persistedVolume,
              conversion_rate: conversionContext.persistedConversionRate,
            },
          )
        : values;

      const initialValuesForSave = conversionContext.shouldUseConvertedView
        ? convertUtilityFormValues(
            initialValues,
            {
              currency: conversionContext.activeCurrency,
              volume: conversionContext.activeVolume,
              conversion_rate: conversionContext.activeConversionRate,
            },
            {
              currency: conversionContext.persistedCurrency,
              volume: conversionContext.persistedVolume,
              conversion_rate: conversionContext.persistedConversionRate,
            },
          )
        : initialValues;

      const response = await updateUtilityCostsData(
        valuesForSave,
        t,
        isUserScope,
        isMakeDefaultConfirmation,
        initialValuesForSave,
      );

      setIsLoading(false);

      if (response) {
        if (!isUserScope) {
          if (response.savedUtilityCosts) {
            dispatch(storePlantUtilityCosts(response.savedUtilityCosts));
          }

          if (!isMakeDefaultConfirmation) {
            onUtilityCostsSaved?.(response.savedUtilityCosts || null);
          }
        }

        const nextDisplayValues = conversionContext.shouldUseConvertedView
          ? convertUtilityFormValues(
              response.formValues,
              {
                currency: conversionContext.persistedCurrency,
                volume: conversionContext.persistedVolume,
                conversion_rate: conversionContext.persistedConversionRate,
              },
              {
                currency: conversionContext.activeCurrency,
                volume: conversionContext.activeVolume,
                conversion_rate: conversionContext.activeConversionRate,
              },
            )
          : response.formValues;

        setInitialValues(nextDisplayValues);
        setValues(nextDisplayValues);
      }
    } else if (!isMakeDefaultConfirmation) {
      setValues({ ...initialValues });
    }
  };

  const handleMakeDefault = () => {
    setShowConfirmation(true);
  };

  const makeDefaultButton = (
    <div className="info-section">
      <IconWithTooltip label={t("utilityCosts.makeDefaultInfo")} />
      <WPTertiaryButton
        id={CustomModalButtons.MAKE_DEFAULT}
        label={t("utilityCosts.makeDefaultLabel")}
        onClick={handleMakeDefault}
        disabled={disableMakeDefault}
        variant="text"
        size="small"
        className="utility-costs-make-default-btn"
      />
    </div>
  );

  return (
    <>
      <CustomModal
        show={open}
        onModalClose={handleModalClose}
        header={t(`${scope}.utilityHeader`)}
        confirmBtn={t("ValueStrings.saveChanges")}
        confirmBtnDisabled={disableActions}
        isLoading={isLoading}
        width="750px"
        customclass="utility-costs-dialog"
        modalFooter={FEATURE_SCOPE.PLANT ? makeDefaultButton : null}
      >
        <div className="utility-costs-body">
          <WPMessageBanner
            type={BANNER_TYPES.info}
            message={t(`${scope}.infoMessage`)}
            className="marginTop0"
          />
          <UtilityCostFields
            values={values}
            currency={activeCurrency}
            volume={activeVolume}
            fieldRanges={fieldRanges}
            onChange={handleChange}
            onValidationError={handleValidationError}
          />
        </div>
      </CustomModal>
      {showConfirmation && (
        <ConfirmationPopup
          type="warning"
          header={confirmDialogConfig.header}
          description={confirmDialogConfig.description}
          confirmBtn={confirmDialogConfig.confirmBtn}
          cancelBtn={confirmDialogConfig.cancelBtn}
          onClose={handleConfirmationClose}
          width="450px"
        />
      )}
    </>
  );
};

export default UtilityCostsDialog;
